import ApplicationController from '../../javascript/controllers/application_controller'
import { track } from '../../javascript/lib/track'
import { ROI_CALCULATED } from '../../javascript/lib/plausible_events'

// Marketing::RoiCalculator Stimulus controller (spec §5, §8).
//
// Shared by RoiCalculatorComponent and RoiTeaserComponent: each declares
// `data-controller="marketing--roi-calculator"` on its own element; only the
// targets it renders are written (hasFooTarget guards every write), so the
// teaser's CTA-link math runs even though it shows only the headline.
//
// No JS-side default values (v2-21): every value arrives from a server-
// rendered data-…-value attribute. An absent/invalid URL param leaves the
// rendered value untouched — never NaN, never a JS literal.
const HOURS_PER_DAY = 8
const DEBOUNCE_MS = 1500

export default class extends ApplicationController {
  static values = {
    employees: Number,
    minutesSavedPerEmployeePerDay: Number,
    workDaysPerYear: Number,
    hourlyRate: Number,
    productivityFactorPercent: Number,
    migrationPersonDays: Number,
    publicPlans: Array,
    source: String,
    locale: String,
    individualOffer: String,
    paybackTemplate: String,
    monthOne: String,
    monthOther: String,
  }

  static targets = [
    'avgPerYear',
    'grossSavings',
    'searchSavings',
    'opex',
    'migrationPerYear',
    'benefit3y',
    'opex3y',
    'capex',
    'net3y',
    'payback',
    'pickedPlan',
    'roiRechnerLink',
  ]

  connect() {
    this._debounceTimer = undefined
    this._applyUrlParamPrefill()
    this._recompute()
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
  }

  onInput(event) {
    const key = event.target.dataset.inputKey
    if (!key) return
    const valueName = `${this._camelize(key)}Value`
    const parsed = Number(event.target.value)
    // employees must be >= 1; all other inputs may be 0 (e.g. a 0% factor).
    const min = key === 'employees' ? 1 : 0
    if (Number.isFinite(parsed) && parsed >= min && valueName in this) {
      this[valueName] = parsed
    }
    this._recompute()
    this._scheduleEvent()
  }

  // --- Math (spec §5) ---

  _compute() {
    const employees = this.employeesValue
    const hourlyRate = this.hourlyRateValue
    const factor = this.productivityFactorPercentValue / 100

    const hoursSaved =
      (employees * this.minutesSavedPerEmployeePerDayValue * this.workDaysPerYearValue) / 60
    const grossSavings = hoursSaved * hourlyRate * factor

    const capex = this.migrationPersonDaysValue * HOURS_PER_DAY * hourlyRate
    const plan = this._pickPlan(employees)
    const opex = plan ? plan.monthly_net_eur * 12 : null

    const netAnnual = opex === null ? null : grossSavings - opex
    const paybackMonths = netAnnual !== null && netAnnual > 0 ? capex / (netAnnual / 12) : null

    // 3-year reconstruction. The headline bakes in the one-time migration by
    // amortizing it (÷3), so the ×3 cross-check is self-consistent (v3-1).
    const benefit3y = grossSavings * 3
    const opex3y = opex === null ? null : opex * 3
    const net3y = opex === null ? null : benefit3y - opex3y - capex
    const avgPerYear = net3y === null ? null : net3y / 3
    const migrationPerYear = capex / 3

    return {
      employees,
      hoursSaved,
      grossSavings,
      capex,
      plan,
      opex,
      netAnnual,
      paybackMonths,
      benefit3y,
      opex3y,
      net3y,
      avgPerYear,
      migrationPerYear,
    }
  }

  _recompute() {
    const c = this._compute()

    // Above the largest public tier (no plan) the gross/migration-derived
    // figures blank to "—" rather than half-render a sum mixing numbers and the
    // offer text; only the offer-bearing targets show the offer (R8).
    const noPlan = c.opex === null

    // Headline figure (v3-1) + the inline breakdown line (v3-3).
    this._write(
      'avgPerYear',
      c.avgPerYear === null ? this.individualOfferValue : this._fmtEuro(c.avgPerYear),
    )
    this._write('grossSavings', noPlan ? '—' : this._fmtEuro(c.grossSavings))
    // The teaser renders only `searchSavings` (the gross figure); it has no tier
    // and is never null, so write it unconditionally via target-presence so the
    // shared controller never breaks the teaser.
    this._write('searchSavings', this._fmtEuro(c.grossSavings))
    this._write('opex', c.opex === null ? this.individualOfferValue : this._fmtEuro(c.opex))
    this._write('migrationPerYear', noPlan ? '—' : this._fmtEuro(c.migrationPerYear))

    // Details: the 3-year reconstruction and the plan name.
    this._write('benefit3y', noPlan ? '—' : this._fmtEuro(c.benefit3y))
    this._write('opex3y', c.opex3y === null ? this.individualOfferValue : this._fmtEuro(c.opex3y))
    this._write('capex', noPlan ? '—' : this._fmtEuro(c.capex))
    this._write('net3y', c.net3y === null ? this.individualOfferValue : this._fmtEuro(c.net3y))
    this._write('payback', this._paybackSentence(c))
    this._write('pickedPlan', c.plan ? String(c.plan.title) : this.individualOfferValue)

    if (this.hasRoiRechnerLinkTarget) {
      const u = new URL(this.roiRechnerLinkTarget.href, window.location.origin)
      u.searchParams.set('employees', String(c.employees))
      this.roiRechnerLinkTarget.href = u.toString()
    }
  }

  // Cheapest public plan whose seat limit covers the headcount; null above the
  // largest tier → caller renders the individual-offer text (spec §5.2, v2-12).
  _pickPlan(employees) {
    const plans = this.publicPlansValue
    if (plans.length === 0) return null
    const sorted = [...plans].sort((a, b) => a.monthly_net_eur - b.monthly_net_eur)
    return sorted.find((p) => employees <= p.user_limit) || null
  }

  // --- Helpers ---

  _write(target, value) {
    const cap = target.charAt(0).toUpperCase() + target.slice(1)
    if (this[`has${cap}Target`]) {
      this[`${target}Targets`].forEach((el) => {
        el.textContent = value
      })
    }
  }

  _fmtEuro(n) {
    if (!Number.isFinite(n)) return '—'
    return new Intl.NumberFormat(this.localeValue, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(n)
  }

  // One plain payback sentence (v3-4) with correct singular/plural unit;
  // "—" when there is no positive net annual saving to amortize against.
  // The migration is the customer's own effort (imputed working time), not a
  // Librario fee — the wording makes that explicit so the figure can't read
  // as an invoice.
  _paybackSentence(c) {
    if (c.paybackMonths === null) return '—'
    const months = Math.round(c.paybackMonths)
    const unit = months === 1 ? this.monthOneValue : this.monthOtherValue
    return this.paybackTemplateValue
      .replace('{capex}', this._fmtEuro(c.capex))
      .replace('{months}', String(months))
      .replace('{unit}', unit)
  }

  // --- Plausible (spec §11) ---

  _props() {
    const c = this._compute()
    return {
      employees_bucket: this._employeesBucket(c.employees),
      net_roi_band: this._netRoiBand(c),
      source: this.sourceValue,
    }
  }

  _employeesBucket(n) {
    if (n < 10) return '1-9'
    if (n < 50) return '10-49'
    if (n < 200) return '50-199'
    if (n < 1000) return '200-999'
    return '1000+'
  }

  _netRoiBand(c) {
    if (c.opex === null) return 'no_plan' // above the largest public tier
    if (c.opex <= 0) return '>1000%'
    const pct = (c.netAnnual / c.opex) * 100
    if (pct < 0) return '<0%'
    if (pct < 100) return '0-100%'
    if (pct < 500) return '100-500%'
    if (pct < 1000) return '500-1000%'
    return '>1000%'
  }

  _scheduleEvent() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => {
      try {
        track(ROI_CALCULATED, { props: this._props() })
      } catch (error) {
        this.reportError(error, { event: 'roi_calculated', source: 'plausible_tracker' })
      }
    }, DEBOUNCE_MS)
  }

  _applyUrlParamPrefill() {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    if (Array.from(params.keys()).length === 0) return
    const map = {
      employees: 'employeesValue',
      minutes_saved_per_employee_per_day: 'minutesSavedPerEmployeePerDayValue',
      work_days_per_year: 'workDaysPerYearValue',
      hourly_rate: 'hourlyRateValue',
      productivity_factor_percent: 'productivityFactorPercentValue',
      migration_person_days: 'migrationPersonDaysValue',
    }
    let consumedAny = false
    for (const [paramKey, valueName] of Object.entries(map)) {
      const raw = params.get(paramKey)
      if (raw === null) continue
      const num = Number(raw)
      const min = paramKey === 'employees' ? 1 : 0
      if (Number.isFinite(num) && num >= min && valueName in this) {
        this[valueName] = num
        const input = this.element.querySelector(`input[data-input-key="${paramKey}"]`)
        if (input) input.value = String(num)
        consumedAny = true
      }
    }
    if (consumedAny) this.sourceValue = 'shared_link'
  }

  _camelize(snake) {
    return snake.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
  }
}
